import { Box, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { Check } from "lucide-react";
import { memo, useCallback } from "react";

type StatementCardProps = {
  /** Statement copy shown to the attestor. */
  text: string;
  /** Whether the user has confirmed this statement. */
  isChecked: boolean;
  /** Whether this statement is required to be confirmed before signing. */
  isRequired: boolean;
  /** Disable interaction (used for read-only / submitted states). */
  isDisabled?: boolean;
  /** Toggle handler — only invoked when not disabled. */
  onToggle?: (next: boolean) => void;
  /** Optional 1-based ordinal shown next to the text for clarity. */
  index?: number;
};

const checkboxSize = "20px";

// A statement card is a "thing to confirm" — it should read like a clean
// list item, not a form with loud labels. Required vs optional is
// communicated through a tiny subdued caption (red for required, grey for
// optional), not via an attention-grabbing tag.
const StatementCard = memo(
  ({
    text,
    isChecked,
    isRequired,
    isDisabled = false,
    onToggle,
    index,
  }: StatementCardProps) => {
    const handleClick = useCallback(() => {
      if (isDisabled) return;
      onToggle?.(!isChecked);
    }, [isChecked, isDisabled, onToggle]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (isDisabled) return;
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onToggle?.(!isChecked);
        }
      },
      [isChecked, isDisabled, onToggle],
    );

    const ariaLabel =
      typeof index === "number"
        ? `Statement ${index}${isRequired ? ", required" : ", optional"}`
        : isRequired
          ? "Required statement"
          : "Optional statement";

    return (
      <Box
        as="div"
        role={isDisabled ? "group" : "checkbox"}
        aria-checked={isDisabled ? undefined : isChecked}
        aria-required={!isDisabled && isRequired ? true : undefined}
        aria-disabled={isDisabled || undefined}
        aria-label={ariaLabel}
        tabIndex={isDisabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        cursor={isDisabled ? "default" : "pointer"}
        position="relative"
        w="full"
        bg={isChecked ? "p.50" : "bg.container"}
        border="1px solid"
        borderColor={isChecked ? "p.300" : "border.default"}
        borderRadius="12px"
        minH="56px"
        // Slightly more left-padding to make room for the optional left rail
        // accent when checked — without shifting the layout.
        pl={{ base: "18px", md: "22px" }}
        pr={{ base: 4, md: 5 }}
        py={{ base: 4, md: 4 }}
        transitionProperty="background-color, border-color, box-shadow, transform"
        transitionDuration="120ms"
        transitionTimingFunction="ease-out"
        opacity={isDisabled ? 0.85 : 1}
        _hover={
          isDisabled
            ? undefined
            : {
                borderColor: isChecked ? "p.400" : "border.hover",
                bg: isChecked ? "p.50" : "bg.subtle",
              }
        }
        _active={
          isDisabled
            ? undefined
            : {
                transform: "scale(0.996)",
              }
        }
        _focusVisible={{
          outline: "none",
          borderColor: "border.focus",
          boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
        }}
      >
        {/* Subtle left rail: 3px Azurite accent when checked. Signals
            "confirmed" without competing visually with the checkbox. */}
        {isChecked && (
          <Box
            position="absolute"
            left={0}
            top="10px"
            bottom="10px"
            width="3px"
            borderRadius="0 3px 3px 0"
            bg="p.500"
            aria-hidden="true"
          />
        )}
        <HStack align="flex-start" spacing={{ base: 3, md: 4 }} w="full">
          {/* Checkbox visual — the entire card is the hit target. */}
          <Box
            position="relative"
            mt="2px"
            flexShrink={0}
            w={checkboxSize}
            h={checkboxSize}
            borderRadius="6px"
            bg={
              isChecked
                ? isDisabled
                  ? "neutral.400"
                  : "success.500"
                : "bg.container"
            }
            border="1.5px solid"
            borderColor={
              isChecked
                ? isDisabled
                  ? "neutral.400"
                  : "success.500"
                : "border.strong"
            }
            display="flex"
            alignItems="center"
            justifyContent="center"
            transitionProperty="background-color, border-color"
            transitionDuration="120ms"
            transitionTimingFunction="ease-out"
            aria-hidden="true"
          >
            {isChecked && (
              <Icon as={Check} boxSize="14px" color="white" strokeWidth={3} />
            )}
          </Box>

          <VStack align="flex-start" spacing="6px" flex="1" minW={0}>
            {/* Numeric prefix + tiny required indicator. Required uses a
                small red dot + caption; optional uses an even more muted
                "Optional" caption. No coloured pills — keeps the card
                surface quiet so the statement text is the focus. */}
            <HStack spacing={1.5} align="center" h="14px">
              {typeof index === "number" && (
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="text.body.tertiary"
                  letterSpacing="0.04em"
                  fontFamily="Archivo, sans-serif"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {String(index).padStart(2, "0")}
                </Text>
              )}
              <Text
                fontSize="10px"
                color="text.body.tertiary"
                letterSpacing="0.06em"
                lineHeight="1"
              >
                ·
              </Text>
              {isRequired ? (
                <HStack spacing="6px" align="center">
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="error.500"
                    flexShrink={0}
                    aria-hidden="true"
                  />
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="error.600"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    lineHeight="1"
                  >
                    Required
                  </Text>
                </HStack>
              ) : (
                <Text
                  fontSize="10px"
                  fontWeight="500"
                  color="text.body.tertiary"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  lineHeight="1"
                >
                  Optional
                </Text>
              )}
            </HStack>

            <Text
              fontSize={{ base: "14px", md: "15px" }}
              fontWeight="500"
              color="text.body.primary"
              lineHeight="1.5"
              sx={{ textWrap: "pretty" }}
            >
              {text}
            </Text>
          </VStack>
        </HStack>
      </Box>
    );
  },
);

StatementCard.displayName = "StatementCard";

export default StatementCard;
